import { useCallback, useContext, useMemo } from 'react'
import {
  type AccessControlConfig,
  hasFlipper,
  hasPermission,
  hasWarehouseLocationFeature,
  flippersToFlipsMapping,
} from 'legacy/components/AccessControl/accessControl.utils'
import { FlipperContext, type Flips, type Flipper } from 'legacy/contexts/flipper/FlipperContext'
import { useRequiredContext } from 'legacy/contexts/utils'
import { WarehouseContext } from 'utils/contexts/warehouse/WarehouseContext'
import { useCurrentAccountContext } from 'utils/current-account/CurrentAccountContext'
import { type Permission } from 'legacy/common/types'

/**
 * Returns true if the access control requirements are met for the
 * current user. Otherwise returns false.
 */
function useAccessControl(): (
  accessControlConfig: AccessControlConfig | AccessControlConfig[]
) => boolean {
  const flipperContext = useRequiredContext(FlipperContext)
  const warehouseContext = useContext(WarehouseContext)
  const currentAccountContext = useCurrentAccountContext()

  const allUserPermissions = useMemo(() => {
    const retailerApiPermissions =
      currentAccountContext?.account?.permissions.map(permission => permission.slug) || []

    return retailerApiPermissions as Permission[]
  }, [currentAccountContext])

  // Flippers / Feature Toggles: We support/combine flippers from both legacy API (deprecated) and modern API
  const allUserFlippers = useMemo(() => {
    const legacyFlippers = flipperContext.flipper
    const currentAccountFlippers = (currentAccountContext?.account?.featureToggles || []).reduce(
      (flips, featureToggle) => {
        flips[featureToggle.name] = true
        return flips
      },
      {}
    ) as Flips

    return { ...legacyFlippers, ...currentAccountFlippers }
  }, [flipperContext.flipper, currentAccountContext])

  // Warehouse Flippers / Feature Toggles: We support/combine flippers from both legacy API (deprecated) and modern API
  const allWarehouseFlippers = useMemo(() => {
    const legacyWarehouseFlippers = warehouseContext?.warehouse?.flippers || []
    const warehouseFeatureToggles = (warehouseContext?.featureToggles || []).map(
      featureToggle => featureToggle.name
    ) as Flipper[]

    return [...legacyWarehouseFlippers, ...warehouseFeatureToggles]
  }, [warehouseContext])

  return useCallback(
    function hasAccess(accessControlConfig: AccessControlConfig | AccessControlConfig[]) {
      return (
        Array.isArray(accessControlConfig) ? accessControlConfig : [accessControlConfig]
      ).some(
        ({
          flippers,
          notFlippers,
          warehouseFlippers,
          notWarehouseFlippers,
          permissions,
          warehouseLocationFeatures,
        }) => {
          if (flippers && !hasFlipper(allUserFlippers, flippers)) {
            return false
          }

          if (notFlippers && hasFlipper(allUserFlippers, notFlippers)) {
            return false
          }

          if (permissions && !hasPermission(allUserPermissions, permissions)) {
            return false
          }

          if (warehouseLocationFeatures || warehouseFlippers || notWarehouseFlippers) {
            if (warehouseContext === null) {
              throw new Error('WarehouseContext required for access permissions')
            }

            /**
             * If the locations aren't loaded yet we throw this promise so
             * that the Suspense API can block the render. We don't need to resolve
             * this due to the Suspense implementation when the hook is re-run after
             * context updates.
             */
            if (!warehouseContext.loaded && !warehouseContext.error) {
              throw new Promise(() => {})
            }

            if (warehouseContext.error) {
              return false
            }

            if (
              warehouseLocationFeatures &&
              !hasWarehouseLocationFeature(
                warehouseContext.locationFeatures,
                warehouseLocationFeatures
              )
            ) {
              return false
            }

            if (warehouseFlippers) {
              const flips = flippersToFlipsMapping(allWarehouseFlippers)

              if (!hasFlipper(flips, warehouseFlippers)) {
                return false
              }
            }

            if (notWarehouseFlippers) {
              const flips = flippersToFlipsMapping(allWarehouseFlippers)

              if (hasFlipper(flips, notWarehouseFlippers)) {
                return false
              }
            }
          }

          return true
        }
      )
    },
    [allUserPermissions, allUserFlippers, warehouseContext, allWarehouseFlippers]
  )
}

export default useAccessControl
